import {
    Card,
    CfwPayloadType,
    FareType,
    MetaDataType,
    SearchData,
    SearchIntentType,
    ServicesData,
    SrpFareObject,
    SubTravelOptions,
    SrpFareObject,
    SpecialRTFareMap,
    BenefitTag
} from '@/types/srp'
import { isEmpty } from '../general/browserHelper'
import { formatCurrency, replacePlaceholders } from '..'
import { getFormattedDate, getFormattedDateCfw } from '../general/date'
import { getUserAuthValues, isUserSignedIn } from '../general/user'
import { listOfJourneyKeys, LOGGED_IN, NON_LOGGED_IN } from '@/constants/Srp'
import { Statsig } from '@/utils/statsig'
import { isTravelMaxSale, getUtmSourceInfo } from '../common'

import { ALLOWED_BENEFIT_TYPES, PTC_BENEFIT_TYPES } from '@/constants/Srp'
import dayjs from 'dayjs'
import { ProcessedData as ProcessedDataType } from '@/utils/srp/types'
import { extractChargeInfosFromPenalties } from '../cfw'
import { PTC_FARE_CATEGORY } from '@/constants/components/fareType'
import { isWowPricingEnabled } from '@/utils/wowPricing'
import { getSmbVasCallout } from '@/utils/flights/smbVasCoupon'

const SEAT_REC_CHEAP_RANGE_AB_FEATURE = 'SEAT_REC_CHEAP_RANGE'

export const getPaxDetails = (paxCriteria: any[]) => {
    let [adults, children, infants] = [0, 0, 0]
    if (isEmpty(paxCriteria) || !Array.isArray(paxCriteria)) {
        return [adults, children, infants]
    }
    paxCriteria.forEach((pax: any) => {
        if (pax?.type === 'ADT') adults = pax.count
        if (pax?.type === 'CHD') children = pax.count
        if (pax?.type === 'INF') infants = pax.count
    })
    return [adults, children, infants]
}

export const isPerAdultPricing = (paxCriteria: any[]) => {
    let [adults, children, infants] = getPaxDetails(paxCriteria)
    if ( !isTravelMaxSale() && (adults + children + infants > 1)) {
        return true
    }
    return false
}

export const checkMultiAirlineCase = (flights: any[]) => {
    const multiAirlinePairsExclude = [
        ['I5', 'IX'],
        ['IX', 'I5']
    ]
    let isMultiAirline = false
    let airlineCode = flights?.[0]?.airlineCode || ''
    flights.forEach(flight => {
        if (
            airlineCode !== flight.airlineCode &&
            !multiAirlinePairsExclude.some(pair => pair[0] === airlineCode && pair[1] === flight.airlineCode)
        ) {
            isMultiAirline = true
        }
    })
    return isMultiAirline
}

export const getFlightInfo = (card: Card, metaData: MetaDataType) => {
    const { summary, travelOptionId, subTravelOptionIds = [], layover = [] } = card || {}
    const [subTravelOptionId] = subTravelOptionIds || []
    const { firstDeparture, lastArrival, stops, timelineText, totalDuration, flights = [], lowestRoundTripPriceDetails = {} } = summary || {}
    const firstFlight = flights?.[0] || {}
    const departureTime = firstDeparture?.airport?.time
    const departureAirportCode = firstDeparture?.airport?.code
    const arrivalTime = lastArrival?.airport?.time
    const arrivalAirportCode = lastArrival?.airport?.code
    const formattedDepartureTime = getFormattedDate(departureTime, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss')
    const formattedArrivalTime = getFormattedDate(arrivalTime, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss')
    const dayDiff = dayjs(formattedArrivalTime).diff(formattedDepartureTime, 'day')
    const isMultiAirline = checkMultiAirlineCase(summary.flights)
    const flightNumber = `${firstFlight.airlineCode}-${firstFlight.flightNumber}`
    const { airlineCode } = firstDeparture || {}
    const airlineName = metaData?.airlineDetail?.[airlineCode]?.name || ''
    const from = metaData?.airportDetail?.[firstDeparture?.airport?.code]?.city || ''
    const to = metaData?.airportDetail?.[lastArrival?.airport?.code]?.city || ''
    const layoverInfo = layover.map((item, index) => {
        return {
            airportCity: metaData?.airportDetail?.[item.airport]?.city || '',
            airportCode: item.airport,
            airportName: metaData?.airportDetail?.[item.airport]?.name.split(' ')[0] || '',
            hours: item.totalDuration.hh,
            minutes: item.totalDuration.mm,
            layoverType: item.totalDuration.hh <= 3 ? 'Short layover' : 'Long layover'
        }
    })
    return {
        flightNumber,
        airlineCode,
        airlineName,
        isMultiAirline,
        departureTime,
        arrivalTime,
        stops,
        timelineText,
        totalDuration,
        travelOptionId,
        from,
        to,
        departureAirportCode,
        arrivalAirportCode,
        subTravelOptionId,
        lowestRoundTripPriceDetails,
        dayDiff,
        layoverInfo
    }
}

export const getPtcFare = (fareIds: string[], fares: SrpFareObject, ptcFareType: keyof typeof PTC_FARE_CATEGORY) => {
    const ptcFareCategory = PTC_FARE_CATEGORY[ptcFareType] || ''
    const ptcFare = fareIds.filter((fareId: string) => {
        return fares?.[fareId]?.fareCategory === ptcFareCategory
    })
    return ptcFare
}

export const getCheckInBaggageFare = (fareIds: string[], fares: SrpFareObject) => {
    const checkInBaggageFares = fareIds.filter((fareId: string) => {
        return fares?.[fareId]?.checkInBaggageAllowed
    })
    return checkInBaggageFares 
}

export const getFaresInfo = (
    card: Card,
    subTravelOptions: SubTravelOptions,
    fares: SrpFareObject,
    sortcards = false,
    isCheckInBaggage = false,
    ptcFareType = ''
) => {
    let finalFareObjectList: any = []
    const { subTravelOptionIds = [], travelOptionId = '' } = card || {}
    subTravelOptionIds.forEach(subTravelOptionId => {
        let {
            fareIds = [],
            cheapestFareId = '',
            subTravelOptionKey = '',
            sortList = [],
            tags,
            totalFareCount = 0
        } = subTravelOptions[subTravelOptionId] || {}
        if (!isEmpty(ptcFareType)) {
            fareIds = getPtcFare(fareIds, fares, ptcFareType as keyof typeof PTC_FARE_CATEGORY)
        } else if (isCheckInBaggage) {
            fareIds = getCheckInBaggageFare(fareIds, fares)
        }
        if (isEmpty(fareIds)) return []
        let fareObjectList: FareType[] = fareIds.map((fareId: string = '') => {
            const isCheapest = cheapestFareId === fareId
            return {
                ...(fares?.[fareId] || {}),
                subTravelOptionId,
                travelOptionId,
                isCheapest,
                subTravelOptionKey,
                tags,
                totalFareCount
            }
        })
        if (sortcards && Array.isArray(sortList) && !isEmpty(sortList)) {
            fareObjectList = sortList
                .map(fareId => {
                    return fareObjectList.find((fareObject: FareType) => fareObject.fareId === fareId)
                })
                .filter(Boolean) as FareType[]
        }
        finalFareObjectList = [...finalFareObjectList, ...fareObjectList]
    })
    return finalFareObjectList
}

/** Cheapest fare among one leg's on-screen cards (date ribbons), or null when none has a fare. */
export const getCheapestCardPrice = (cards: any[] | undefined, searchData: any): number | null => {
    try {
        const cheapest = (cards || []).reduce((min: number, card: any) => {
            const faresObjectList = getFaresInfo(card, searchData?.subTravelOptions, searchData?.fares)
            const cheapestFare = faresObjectList.find((f: any) => f.isCheapest)?.pricing || {}
            const pricing = isTravelMaxSale()
                ? cheapestFare?.totalPricing?.totalPrice
                : cheapestFare?.perAdultPricing?.totalPrice
            return pricing ? Math.min(min, pricing) : min
        }, Number.MAX_VALUE)
        return cheapest === Number.MAX_VALUE ? null : cheapest
    } catch (e) {
        return null
    }
}

export const getCouponCallout = (couponDetails, showPerAdultPricing) => {
    if (isEmpty(couponDetails)) return { couponCallout: '', couponCode: '' }
    const { adultCouponMessage, couponCode, discountAmount, discountedPrice, message } = couponDetails || {}
    let couponMessage = (showPerAdultPricing ? adultCouponMessage : message) || ''
    if (couponMessage) {
        couponMessage = couponMessage
            .replace('{couponCode}', couponCode)
            .replace('{discountAmount}', formatCurrency(discountAmount))
        return { couponCallout: couponMessage, couponCode }
    }
    return { couponCallout: '', couponCode }
}

export const getSlasherCouponCode = ({ fare, showPerAdultPricing, isRT = false }) => {
    if (isRT || isEmpty(fare)) return ''
    const { pricing } = fare || {}
    const pricingObject = showPerAdultPricing ? pricing?.perAdultPricing : pricing?.totalPricing
    return pricingObject?.couponDetails?.slasherCouponCode || ''
}

/** nepDetails rides on every RT fare even with no discount, so hasNepStrikeOff is the only safe gate. */
export const getRtNepDetails = (couponObj: { nepDetails?: any } | undefined) => {
    const { slasherCouponCode, strikeOffDetails } = couponObj?.nepDetails || {}
    // Number(), not a `= 0` default: the backend sends explicit nulls.
    const strikeOffPrice = Number(strikeOffDetails?.strikeOffPrice) || 0
    const displayPrice = Number(strikeOffDetails?.displayPrice) || 0
    const discountAmount = Number(strikeOffDetails?.discountAmount) || 0
    const hasNepStrikeOff = strikeOffPrice > 0 && Math.floor(strikeOffPrice) > Math.floor(displayPrice)
    return { slasherCouponCode: slasherCouponCode || '', strikeOffPrice, displayPrice, discountAmount, hasNepStrikeOff }
}

/** The backend's own discount when it sends one, else the strike-off delta. */
export const getNepDiscountAmount = (nepDetails: ReturnType<typeof getRtNepDetails>) =>
    nepDetails.hasNepStrikeOff
        ? nepDetails.discountAmount || Math.max(nepDetails.strikeOffPrice - nepDetails.displayPrice, 0)
        : 0

export const getPricingDetails = ({ strikeOffDetails, totalPrice }) => {
    let displayPrice = totalPrice
    let strikeOffPrice = strikeOffDetails?.strikeOffPrice
    let discountAmount = strikeOffDetails?.discountAmount
    if (!isEmpty(strikeOffPrice)) {
        displayPrice = strikeOffDetails.displayPrice
    }
    return { displayPrice, strikeOffPrice, discountAmount }
}

export const getLowestRoundTripPriceDetails = (lowestRoundTripPriceDetails: any, isPerAdultPricing: boolean) => {
  return isPerAdultPricing ? lowestRoundTripPriceDetails?.perAdultPricing : lowestRoundTripPriceDetails?.totalPricing
}

export const getFarePricingAttributes = ({ fare, showPerAdultPricing }) => {
    if (isEmpty(fare)) return {}
    let { pricing, fareId } = fare || {}
    const pricingObject = showPerAdultPricing ? pricing.perAdultPricing : pricing.totalPricing
    const {
        couponDetails,
        strikeOffDetails,
        totalBaseFare,
        totalTax,
        totalPrice,
        amendInsuranceCharges,
        cancelInsuranceCharges,
        travelInsuranceCharges,
        visaRejectionCoverCharges
    } = pricingObject
    const { couponCallout, couponCode } = getCouponCallout(couponDetails, showPerAdultPricing)
    const { displayPrice, strikeOffPrice, discountAmount } = getPricingDetails({
        strikeOffDetails,
        totalPrice
    })
    return {
        strikeOffPrice,
        displayPrice,
        couponCallout,
        smbVasCouponCallout: getSmbVasCallout(couponDetails),
        couponCode,
        totalBaseFare,
        totalTax,
        totalPrice,
        amendInsuranceCharges,
        cancelInsuranceCharges,
        travelInsuranceCharges,
        visaRejectionCoverCharges,
        fareId,
        discountAmount
    }
}

export const getCheapestFareInCard = (cardsList: Card[], subTravelOptions: any, fares: any, isCheckInBaggageEnabled = false, onwardFilterPtcType = '', returnFilterPtcType = '') => {
    if (Array.isArray(cardsList)) {
        return cardsList.map((card, index) => {
            const ptcFareType = index === 0 ? onwardFilterPtcType : returnFilterPtcType
            const fareList = getFaresInfo(card, subTravelOptions, fares, false, isCheckInBaggageEnabled, ptcFareType)
            if (!isEmpty(ptcFareType)) {
                return fareList?.[0] || {}
            }
            return fareList.find(fare => {
                const { isCheapest } = fare || {}
                return isCheapest
            })
        })
    }
    return []
}

export const getSearchIntentDetails = (searchIntent: SearchIntentType) => {
    const { J1, J2 } = searchIntent || {}
    const J1from = J1?.origin
    const J1to = J1?.destination
    const { paxCriteria, departDate = '' } = J1 || {}
    const J1travelDate = getFormattedDateCfw(departDate).toUpperCase()

    const J2from = J2?.origin
    const J2to = J2?.destination
    const J2departDate = J2?.destination || ''
    const J2travelDate = getFormattedDateCfw(J2departDate).toUpperCase()

    return { J1from, J1to, J1travelDate, J2from, J2to, J2travelDate, paxCriteria }
}

export const getSpecialRtFaresInfo = (faresList: string[], cardDetail: Card, subTravelOptions: SubTravelOptions, fares: SrpFareObject) => {
    let finalFareObjectList: any = []
    const { subTravelOptionIds = [], travelOptionId = '' } = cardDetail || {}
    subTravelOptionIds.forEach(subTravelOptionId => {
        const {
            cheapestFareId = '',
            subTravelOptionKey = '',
            tags
        } = subTravelOptions[subTravelOptionId] || {}
        const fareIds = faresList
        if (isEmpty(fareIds)) return []
        let fareObjectList =
            fareIds.map((fareId: string = '') => {
                const isCheapest = cheapestFareId === fareId
                return {
                    ...(fares?.[fareId] || {}),
                    subTravelOptionId,
                    travelOptionId,
                    isCheapest,
                    subTravelOptionKey,
                    tags
                }
        }) || []
        finalFareObjectList = fareObjectList
    })
    return finalFareObjectList
}

export const getSpecialRTFareCase = (onwardFlight: Card, returnFlight: Card, fares: SrpFareObject, isCheckInBaggageEnabled?: boolean, specialRTFareMap?: SpecialRTFareMap, ptcFareType?: keyof typeof PTC_FARE_CATEGORY | '') => {
    const {
        travelOptionId: onwardTravelOptionId
    } = onwardFlight || {}
    const {
        travelOptionId: returnTravelOptionId
    } = returnFlight || {}
    let specialRtFareCount = 0
    let showInterstitialForSplRt = false
    const key = `[${onwardTravelOptionId}, ${returnTravelOptionId}]`
    if (!isEmpty(specialRTFareMap)) {
        const { totalFareCount = 0 , showInterstitial = false } = specialRTFareMap?.[key] || {}
        specialRtFareCount = totalFareCount
        showInterstitialForSplRt = showInterstitial
    }
    let fareList = specialRTFareMap?.[key]?.fareIds || []
    if (!isEmpty(fares) && !isEmpty(ptcFareType)) {
        fareList = getPtcFare(fareList, fares, ptcFareType as keyof typeof PTC_FARE_CATEGORY) || []
    }
    if (!isEmpty(fares) && isCheckInBaggageEnabled) {
        fareList = getCheckInBaggageFare(fareList, fares) || []
    }
    return { fareList, specialRtFareCount, showInterstitialForSplRt }
}

export const selectedFlightsDetails = ({
    selectedFlight,
    metaData,
    subTravelOptions,
    fares,
    showPerAdultPricing,
    isCheckInBaggageEnabled = false,
    ptcFareType = ''
}: {
    selectedFlight: any
    metaData: any
    subTravelOptions: any
    fares: any
    showPerAdultPricing: boolean
    isCheckInBaggageEnabled: boolean
    ptcFareType?: string
}) => {
    if (isEmpty(selectedFlight)) return {}
    let isFareAvailable = false
    const { departureTime, arrivalTime, airlineCode, isMultiAirline, travelOptionId, stops, totalDuration } = getFlightInfo(
        selectedFlight,
        metaData
    )
    const fareData = getFaresInfo(selectedFlight, subTravelOptions, fares, false, isCheckInBaggageEnabled, ptcFareType) || []
    if (isEmpty(fareData)) {
        isFareAvailable = false
    } else {
        isFareAvailable = true
    }
    let cheapestFare = fareData.find(fare => fare?.isCheapest)
    if (isEmpty(cheapestFare)) {
        cheapestFare = fareData[0]
    }
    const { displayPrice = 0, strikeOffPrice = 0, fareId = '' } = getFarePricingAttributes({
        fare: cheapestFare,
        showPerAdultPricing
    })
    const { totalBaseFare, totalTax, totalPrice } = getFarePricingAttributes({
        fare: cheapestFare,
        showPerAdultPricing: false
    })
    const formatedDepartureTime = getFormattedDate(departureTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
    const formatedArrivalTime = getFormattedDate(arrivalTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
    const flightDuration = `${totalDuration.hh ? `${totalDuration.hh}h` : ''} ${
        totalDuration.mm ? `${totalDuration.mm}m` : ''
    }`
    return {
        displayPrice,
        strikeOffPrice,
        formatedDepartureTime,
        formatedArrivalTime,
        airlineCode,
        isMultiAirline,
        travelOptionId,
        fareId,
        stops,
        flightDuration,
        totalBaseFare,
        totalTax,
        totalPrice,
        isFareAvailable,
        cheapestFare
    }
}

export const getSpecialRTDetails = ({
    selectedFares,
    selectedFlights,
    specialRTFareMap,
    fares,
    showPerAdultPricing,
    isCheckInBaggageEnabledOw,
    isCheckInBaggageEnabledRt,
    onwardPtcFareType,
    returnPtcFareType
}: {
    selectedFares: FareType[]
    selectedFlights: Card[]
    specialRTFareMap: SpecialRTFareMap
    fares: SrpFareObject
    showPerAdultPricing: boolean
    isCheckInBaggageEnabledOw: boolean
    isCheckInBaggageEnabledRt: boolean
    onwardPtcFareType: keyof typeof PTC_FARE_CATEGORY | ''
    returnPtcFareType: keyof typeof PTC_FARE_CATEGORY | ''
}) => {
    let isSpecialRT = false
    const [onwardFlight, returnFlight] = selectedFlights || []
    const [onwardFare, returnFare] = selectedFares || []
    const {
        travelOptionId: onwardTravelOptionId
    } = onwardFlight || {}
    const {
        travelOptionId: returnTravelOptionId
    } = returnFlight || {}
    const key = `[${onwardTravelOptionId}, ${returnTravelOptionId}]`
    let specialRtFareList = specialRTFareMap?.[key]?.fareIds || []
    if (isEmpty(onwardFare) || isEmpty(returnFare)) {
        const isCheckInBaggageEnabled = isCheckInBaggageEnabledOw || isCheckInBaggageEnabledRt
        const ptcFareType = onwardPtcFareType || returnPtcFareType
        const { fareList } = getSpecialRTFareCase(onwardFlight, returnFlight, fares, isCheckInBaggageEnabled, specialRTFareMap, ptcFareType)
        specialRtFareList = fareList || []
        isSpecialRT = !isEmpty(specialRtFareList)
    } else if (!isEmpty(specialRtFareList)) {
        const { displayPrice: onwardFarePrice = 0 } = getFarePricingAttributes({
            fare: onwardFare,
            showPerAdultPricing
        })
        const { displayPrice: returnFarePrice = 0 } = getFarePricingAttributes({
            fare: returnFare,
            showPerAdultPricing
        })
        const totalPrice = onwardFarePrice + returnFarePrice
        const [specialRtFareId] = specialRtFareList || []
        const { displayPrice: specialRtFarePrice = 0 } = getFarePricingAttributes({
            fare: fares?.[specialRtFareId] || {},
            showPerAdultPricing
        })
        if (specialRtFarePrice < totalPrice) {
            isSpecialRT = true
        }
    }
    return {
        isSpecialRT,
        specialRtFareList
    }
}

export const getSpecialRTFareContext = ({
    onwardCard,
    returnCard,
    subTravelOptions,
    fares,
    specialRTFareMap,
    isCheckInBaggageEnabledOw = false,
    isCheckInBaggageEnabledRt = false,
    onwardPtcFareType = '',
    returnPtcFareType = '',
    showPerAdultPricing = false
}: {
    onwardCard: Card
    returnCard: Card
    subTravelOptions: SubTravelOptions
    fares: SrpFareObject
    specialRTFareMap: SpecialRTFareMap
    isCheckInBaggageEnabledOw?: boolean
    isCheckInBaggageEnabledRt?: boolean
    onwardPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
    returnPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
    showPerAdultPricing?: boolean
}) => {
    const onwardFare =
        getFaresInfo(onwardCard, subTravelOptions, fares, false, isCheckInBaggageEnabledOw, onwardPtcFareType)?.[0] ||
        {}
    const returnFare =
        getFaresInfo(returnCard, subTravelOptions, fares, false, isCheckInBaggageEnabledRt, returnPtcFareType)?.[0] ||
        {}
    const { isSpecialRT, specialRtFareList = [] } = getSpecialRTDetails({
        selectedFares: [onwardFare, returnFare],
        selectedFlights: [onwardCard, returnCard],
        specialRTFareMap,
        fares,
        showPerAdultPricing,
        isCheckInBaggageEnabledOw,
        isCheckInBaggageEnabledRt,
        onwardPtcFareType,
        returnPtcFareType
    })

    return {
        isSpecialRT,
        specialRtFareList,
        specialRtLeadFareId: specialRtFareList?.[0] || ''
    }
}

export const getLeadFareIdForCard = (
    card: Card,
    subTravelOptions: SubTravelOptions,
    fares: SrpFareObject,
    specialRtContext?: { isSpecialRT?: boolean; specialRtLeadFareId?: string }
) => {
    if (specialRtContext?.isSpecialRT && specialRtContext.specialRtLeadFareId) {
        return specialRtContext.specialRtLeadFareId
    }
    return getFaresInfo(card, subTravelOptions, fares)?.[0]?.fareId || ''
}

export const getMasterTuppleProps = ({
    selectedFlights,
    metaData,
    subTravelOptions,
    fares,
    showPerAdultPricing,
    specialRTFareMap,
    isCheckInBaggageEnabledOw = false,
    isCheckInBaggageEnabledRt = false,
    onwardPtcFareType = '',
    returnPtcFareType = ''
}: {
    selectedFlights: any[]
    metaData: any
    subTravelOptions: any
    fares: any
    showPerAdultPricing: boolean
    specialRTFareMap: SpecialRTFareMap
    isCheckInBaggageEnabledOw?: boolean
    isCheckInBaggageEnabledRt?: boolean
    onwardPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
    returnPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
}) => {
    const [onwardFlight, returnFlight] = selectedFlights || []
    

    const onwardLegData = selectedFlightsDetails({
        selectedFlight: onwardFlight,
        metaData,
        subTravelOptions,
        fares,
        showPerAdultPricing,
        isCheckInBaggageEnabled: isCheckInBaggageEnabledOw,
        ptcFareType: onwardPtcFareType
    })
    const {
        displayPrice: onwardFlightPrice = 0,
        strikeOffPrice: onwardStrikeOffPrice = 0,
        formatedDepartureTime: onwardFormatedDepartureTime = '',
        formatedArrivalTime: onwardFormatedArrivalTime = '',
        airlineCode: onwardAirlineCode = '',
        isMultiAirline: isOnwardMultiAirline = false,
        travelOptionId: onwardTravelOptionId = '',
        fareId: onwardFareId = '',
        stops: onwardStops = 0,
        flightDuration: onwardFlightDuration = '',
        totalBaseFare: onwardTotalBaseFare = 0,
        totalTax: onwardTotalTax = 0,
        totalPrice: onwardTotalPrice = 0,
        isFareAvailable: onwardFareAvailable,
        cheapestFare: onwardFare
    } = onwardLegData || {}

    const returnLegData = selectedFlightsDetails({
        selectedFlight: returnFlight,
        metaData,
        subTravelOptions,
        fares,
        showPerAdultPricing,
        isCheckInBaggageEnabled: isCheckInBaggageEnabledRt,
        ptcFareType: returnPtcFareType
    })
    const {
        displayPrice: returnFlightPrice = 0,
        strikeOffPrice: returnStrikeOffPrice = 0,
        formatedDepartureTime: returnFormatedDepartureTime = '',
        formatedArrivalTime: returnFormatedArrivalTime = '',
        airlineCode: returnAirlineCode = '',
        isMultiAirline: isReturnMultiAirline = false,
        travelOptionId: returnTravelOptionId = '',
        fareId: returnFareId = '',
        stops: returnStops = 0,
        flightDuration: returnFlightDuration = '',
        totalBaseFare: returnTotalBaseFare = 0,
        totalTax: returnTotalTax = 0,
        totalPrice: returnTotalPrice = 0,
        isFareAvailable: returnFareAvailable,
        cheapestFare: returnFare
    } = returnLegData || {}

    const { isSpecialRT, specialRtFareList } = getSpecialRTDetails({
        selectedFares: [onwardFare, returnFare],
        selectedFlights: [onwardFlight, returnFlight],
        specialRTFareMap,
        fares,
        showPerAdultPricing,
        isCheckInBaggageEnabledOw,
        isCheckInBaggageEnabledRt,
        onwardPtcFareType,
        returnPtcFareType
    })

    let totalBaseFare = onwardTotalBaseFare + returnTotalBaseFare
    let totalTax = onwardTotalTax + returnTotalTax
    let totalPrice = onwardTotalPrice + returnTotalPrice
    const [specialRTFareId = ''] = specialRtFareList || []
    let totalFare = 0
    let totalStrikeOffPrice = 0
    const actualFlightPrice = onwardFlightPrice + returnFlightPrice  
    if (isSpecialRT && specialRTFareId) {
        const selectedfare = fares?.[specialRTFareId]
        const { totalPrice: specialRTTotalPrice, totalBaseFare: specialRTTotalBaseFare, totalTax: specialRTTotalTax } = getFarePricingAttributes({
            fare: selectedfare,
            showPerAdultPricing : false
        })
        const { displayPrice: specialRTPrice  } = getFarePricingAttributes({
            fare: selectedfare,
            showPerAdultPricing
        })
        totalTax = specialRTTotalTax
        totalBaseFare = specialRTTotalBaseFare
        totalPrice = specialRTTotalPrice 
        totalFare = specialRTPrice
        totalStrikeOffPrice = (onwardStrikeOffPrice || onwardFlightPrice || 0) + (returnStrikeOffPrice || returnFlightPrice || 0)
    } else {
        totalFare = actualFlightPrice
        totalStrikeOffPrice = 0
    }

    return {
        totalFare,
        totalStrikeOffPrice,
        onwardFlightPrice,
        onwardFormatedDepartureTime,
        onwardFormatedArrivalTime,
        onwardAirlineCode,
        onwardTravelOptionId,
        isOnwardMultiAirline,
        onwardFareId,
        onwardStops,
        onwardFlightDuration,
        returnFlightPrice,
        returnFormatedDepartureTime,
        returnFormatedArrivalTime,
        returnAirlineCode,
        isReturnMultiAirline,
        returnTravelOptionId,
        returnFareId,
        returnStops,
        returnFlightDuration,
        isSpecialRT,
        specialRTFareId,
        totalBaseFare,
        totalTax,
        totalPrice,
        onwardFareAvailable,
        returnFareAvailable
    }
}

export const findKey = (obj: { [x: string]: any }, targetKey: string, results = []) => {
    for (let key in obj) {
        if (key === targetKey) {
            results.push(obj[key])
        } else if (typeof obj[key] === 'object') {
            findKey(obj[key], targetKey, results)
        }
    }
    return results
}

export const createCopunsPayload = ({
    selectedFlights,
    subTravelOptions,
    fares,
    specialRTFareMap,
    searchIntent,
    dataId,
    searchId = '',
    supercoins,
    processedData,
    isCheckInBaggageEnabledOw,
    isCheckInBaggageEnabledRt,
    onwardPtcFareType = '',
    returnPtcFareType = '',
    sourceType = 'SEARCH'
}: {
    selectedFlights: Card[]
    subTravelOptions: SubTravelOptions
    fares: SrpFareObject
    specialRTFareMap: SpecialRTFareMap
    searchIntent: SearchIntentType
    dataId: string
    searchId?: string
    supercoins: any
    processedData: ProcessedDataType
    isCheckInBaggageEnabledOw?: boolean
    isCheckInBaggageEnabledRt?: boolean
    onwardPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
    returnPtcFareType?: keyof typeof PTC_FARE_CATEGORY | ''
    sourceType?: 'SEARCH' | 'INTERSTITIAL'
}) => {
    const requestedIds = selectedFlights.map((flight: Card) => flight.travelOptionId)
    const key = `[${requestedIds.join(', ')}]`
    const onwardFare = getFaresInfo(selectedFlights[0], subTravelOptions, fares, false, isCheckInBaggageEnabledOw, onwardPtcFareType)?.[0] || {}
    const returnFare = getFaresInfo(selectedFlights[1], subTravelOptions, fares, false, isCheckInBaggageEnabledRt, returnPtcFareType)?.[0] || {}
    const { isSpecialRT, specialRtFareList = [] } = getSpecialRTDetails({
        selectedFares: [onwardFare, returnFare],
        selectedFlights: [selectedFlights[0], selectedFlights[1]],
        specialRTFareMap,
        fares,
        showPerAdultPricing: isPerAdultPricing(searchIntent?.J1?.paxCriteria || []),
        isCheckInBaggageEnabledOw,
        isCheckInBaggageEnabledRt,
        onwardPtcFareType,
        returnPtcFareType
    })
    const travelOptions = selectedFlights.map((flight: Card, index: number) => {
        const { travelOptionId } = flight || {}
        const isCheckInBaggageEnabled = index === 0 ? isCheckInBaggageEnabledOw : isCheckInBaggageEnabledRt
        const ptcFareType = index === 0 ? onwardPtcFareType : returnPtcFareType
        const listOfFares = isSpecialRT
            ? specialRtFareList.map((fareId: string = '') => ({
                  ...(fares?.[fareId] || {}),
                  fareId
              }))
            : getFaresInfo(flight, subTravelOptions, fares, false, isCheckInBaggageEnabled, ptcFareType) || []
        return {
            [travelOptionId]: {
                travelOptionId,
                fareIds: listOfFares.map((fare: any) => {
                    const { fareId, fareGroup = '', fareCategory = '' } = fare || {}
                    return {
                        fareId,
                        fareGroup,
                        fareCategory
                    }
                })
            }
        }
    })
    let fareIdsMap = [{}]
    if (requestedIds.length > 1 && isSpecialRT) {
        fareIdsMap[0] = {
            [key]: {
                travelOptionIds: requestedIds,
                fareIds: specialRtFareList?.map((fareId: string = '') => {
                    const { fareGroup = '', fareCategory = '' } = fares?.[fareId] || {}
                    return {
                        fareId,
                        fareGroup,
                        fareCategory
                    }
                })
            }
        }
    }
    const ffSC = new URLSearchParams(window.location.search).get("isFFSC") === 'true';
    const { trafficSource, utmCustom } = getUtmSourceInfo()
    const payload = {
        dataId,
        searchId,
        searchIntent,
        sourceType,
        couponIntents: {
            requestedIds: [requestedIds],
            travelOptions,
            travelOptionIdsToFareIdsMap: fareIdsMap
        },
        supercoins,
        ffSC,
        international: processedData?.isIntl || false,
        trafficSource,
        utmCustom
    }
    return { key, payload }
}

export const getCouponDisplayAndDiscountValue = ({
    selectedFares,
    showPerAdultPricing,
    isRT,
    couponData,
    cardKey,
    isSpecialRT = false
}) => {
    try {
        let couponDiscountDisplayValue = ''
        let fareKey = ''
        if (isSpecialRT && selectedFares?.[0]?.fareId) {
            const fareId = selectedFares[0].fareId
            const entry = couponData?.[cardKey]?.[`[${fareId}]`]
            const couponObj = (showPerAdultPricing ? entry?.perAdultCouponDetails : entry?.couponDetails) || {}
            const inline = getFarePricingAttributes({
                fare: selectedFares[0],
                showPerAdultPricing
            })
            return {
                couponDiscountDisplayValue: inline.couponCallout || '',
                discountAmount: 0,
                couponCode: inline.couponCode || '',
                smbVasCouponCallout: getSmbVasCallout(couponObj) || inline.smbVasCouponCallout || '',
                nepDetails: getRtNepDetails(couponObj)
            }
        }

        // OW's callout lives on the fare itself; both RT shapes read the coupon map instead.
        if (!isRT && selectedFares?.[0]) {
            let { couponCallout = '', couponCode, smbVasCouponCallout = '' } = getFarePricingAttributes({
                fare: selectedFares[0],
                showPerAdultPricing
            })
            return {
                couponDiscountDisplayValue: couponCallout,
                discountAmount: 0,
                couponCode,
                smbVasCouponCallout,
                nepDetails: getRtNepDetails(undefined)
            }
        }
        const { fareId: onwardFareId } = selectedFares?.[0] || {}
        const { fareId: returnFareId } = selectedFares?.[1] || {}
        if (onwardFareId && (isSpecialRT || returnFareId)) {
            const cardData = couponData?.[cardKey] || {}
            fareKey = isSpecialRT ? `[${onwardFareId}]` : `[${onwardFareId}, ${returnFareId}]`
            const { couponDetails, perAdultCouponDetails } = cardData?.[fareKey] || {}
            const couponObj = (showPerAdultPricing ? perAdultCouponDetails : couponDetails) || {}
            const { discountAmount, couponCode, message, adultCouponMessage } = couponObj || {}
            if (!isEmpty(couponObj)) {
                couponDiscountDisplayValue = (showPerAdultPricing ? adultCouponMessage : message)
                    .replace('{discountAmount}', formatCurrency(discountAmount))
                    .replace('{couponCode}', couponCode)
            }
            return {
                couponDiscountDisplayValue,
                discountAmount,
                couponCode,
                smbVasCouponCallout: getSmbVasCallout(couponObj),
                nepDetails: getRtNepDetails(couponObj)
            }
        }
        return { couponDiscountDisplayValue: '', discountAmount: 0, couponCode: '', smbVasCouponCallout: '', nepDetails: getRtNepDetails(undefined) }
    } catch (e) {
        return { couponDiscountDisplayValue: '', discountAmount: 0, couponCode: '', smbVasCouponCallout: '', nepDetails: getRtNepDetails(undefined) }
    }
}

const getSelectedCardsIndex = (selectedFares: any[], cards: { [x: string]: any[] }) => {
    let selectedCardsIndex: any = []
    selectedFares.forEach((fare, index) => {
        const card = cards[listOfJourneyKeys[index]]?.find(card => card.travelOptionId === fare.travelOptionId) || {}
        selectedCardsIndex.push(card?.cardId)
    })
    return selectedCardsIndex
}

const getFfAbFeatures = (
    supercoinVariant,
    isCouponRevaluate,
    wowPricingVariant,
    isSeatRecCheapRange: boolean,
    isPE = false
) => {
    let abFeatures = []
    if (supercoinVariant === 'default_on') {
        abFeatures.push('SUPER_COIN_TOGGLE_ENABLED')
    }
    if (isCouponRevaluate) {
        abFeatures.push('COUPON_REEVALUATE_WITH_SUPERCOINS')
    }
    if (isSeatRecCheapRange) {
        abFeatures.push(SEAT_REC_CHEAP_RANGE_AB_FEATURE)
    }
    if (isWowPricingEnabled(wowPricingVariant)) {
        abFeatures.push('WOW_PRICE')
    }
    // pe_discovery_v1 — BE uses this on create-itinerary to attach PE cabin matrix /
    // CABIN_UPGRADE_CALLOUT on the itinerary response (mirrors interstitial request `isPE`).
    if (isPE) {
        abFeatures.push('isPE')
    }
    return abFeatures;
}

export const getCreateCallPayloadForCfw: (
    selectedFares: FareType[],
    searchData: SearchData,
    defaultCoupon: string,
    isFFFlow: boolean,
    supercoinVariant: string,
    processedData: ProcessedDataType,
    wowPricingVariant?: string,
    isSeatRecCheapRange?: boolean,
    isPE?: boolean
) => CfwPayloadType = (
    selectedFares,
    searchData,
    defaultCoupon = '',
    isFFFlow = false,
    supercoinVariant = '',
    processedData,
    wowPricingVariant = '',
    isSeatRecCheapRange = false,
    isPE = false
) => {
    const { searchId, searchIntent, cards: resultCardsList, fares } = searchData || {}
    const loggedInStatus = isUserSignedIn() ? LOGGED_IN : NON_LOGGED_IN
    const { userId = '' } = getUserAuthValues() || {}
    const sft = new URLSearchParams(window.location.search).get("sft") || "";
    const od = true
    const isFF = !(new URLSearchParams(window.location.search).get("isCfw") === 'true');
    const isMultiFare = true


    const sectors = listOfJourneyKeys
        .map((key, index) => {
            const sectorData = searchIntent[key]
            if (isEmpty(sectorData) || !sectorData) {
                return ''
            }
            const { origin = '', destination = '', departDate, cabin, paxCriteria = [] } = sectorData
            return {
                index: index + 1,
                origin: origin,
                destination: destination,
                departDate: departDate,
                cabinType: cabin,
                journeyId: key,
                paxInfos: paxCriteria.map(pax => ({
                    paxType: pax.type,
                    paxCount: pax.count
                }))
            }
        })
        .filter(sector => !isEmpty(sector))
    let isCouponRevaluate = getFFCookieValue() === 'V7_FF_SC'
    let abFeatures =
        getFfAbFeatures(supercoinVariant, isCouponRevaluate, wowPricingVariant, isSeatRecCheapRange, isPE) || []
    const { trafficSource, utmCustom } = getUtmSourceInfo()
    let payload = {
        searchId: searchId,
        queryContext: {
            searchIntents: {
                sectors,
                sft
            },
            travelOptionMap: {},
            specialRtData: {}
        },
        responseContext: {
            domain: 'IN',
            currency: 'INR',
            responseVersion: 'V1'
        },
        demandContext: {
            userId,
            loggedInStatus,
            trafficSource,
            utmCustom
        },
        metaDataContext: {
            defaultCoupon : isFF ? "" : defaultCoupon,
            abFeatures,
            trackingDetails: {
                selectedCardListId: []
            },
            od: od,
            multiFare: isMultiFare
        }
    }
    const {
        subTravelOptionId: subTravelOptionIdJ1,
        fareId: fareIdJ1,
        pricing: pricingJ1,
        travelOptionId: travelOptionIdJ1
    } = selectedFares?.[0] || {}
    payload.queryContext.travelOptionMap = {
        J1: {
            subTravelOptions: [
                {
                    subTravelOptionId: subTravelOptionIdJ1,
                    fareId: fareIdJ1,
                    price: pricingJ1?.totalPricing?.totalPrice || ''
                }
            ],
            travelOptionId: travelOptionIdJ1
        }
    }
    if (!isEmpty(selectedFares?.[1])) {
        const {
            subTravelOptionId: subTravelOptionIdJ2,
            fareId: fareIdJ2,
            pricing: pricingJ2,
            travelOptionId: travelOptionIdJ2
        } = selectedFares[1]
        payload.queryContext.travelOptionMap = {
            ...payload.queryContext.travelOptionMap,
            J2: {
                subTravelOptions: [
                    {
                        subTravelOptionId: subTravelOptionIdJ2,
                        fareId: fareIdJ2,
                        price: pricingJ2?.totalPricing?.totalPrice
                    }
                ],
                travelOptionId: travelOptionIdJ2
            }
        }
    }
    payload.metaDataContext.trackingDetails.selectedCardListId = getSelectedCardsIndex(selectedFares, resultCardsList)
    return payload
}

export const getServicesDataCacheKey = (data: any) => {
    const { travelOptions = {}, benefits = {}, baggageAllowances = {} } = data || {}
    let cardIdToBenefitsMap = {
        ...travelOptions
    }
    return {
        cardIdToBenefitsMap,
        benefits,
        baggageAllowances
    }
}

export const getCardServicesData = ({
    servicesData,
    travelOptionId,
    fareId
}: {
    servicesData: ServicesData
    travelOptionId: string
    fareId: string
}) => {
    const { cardIdToBenefitsMap } = servicesData || {}
    const serviceData = cardIdToBenefitsMap[travelOptionId] || {}
    const { subTravelOptions = [] } = serviceData || {}
    let cardServicesData = {}
    
    subTravelOptions.forEach((subTravelOption: any) => {
        const { benefits } = subTravelOption || {}
        if (!isEmpty(benefits?.[fareId])) {            
            cardServicesData = benefits[fareId]
        }
    })
    return cardServicesData
}

export const getCommonCardServicesData = ({
    servicesData,
    travelOptionId,
    fareId,
    isSpecialRT = false
}: {
    servicesData: any // Using any since this accepts commonServicesData with fareBenefits property
    travelOptionId: string
    fareId: string
    isSpecialRT?: boolean
}) => {
    const { fareBenefits, cards } = servicesData || {}
    const fareBenefitData = fareBenefits?.[fareId] || {};
    const specialRtChargeInfos = {
        onwardsChargeInfos: [],
        returnChargeInfos: []
    }
    const {
        baggageAllowanceId: topLevelBaggageAllowanceId = '',
        benefitIds: topLevelBenefitIds = [],
        penalties: topLevelPenalty = [],
        subTravelOptionBenefits : subTravelOptionBenefits = {}
    } = fareBenefitData
    
    // need to handle the subtravelId for onwards and return for SpecialRt case
    let onwardsSubTravelOptionId = "", returnSubTravelOptionId = "";
    // For non specialrt case
    const subTravelOptionId = Object.keys(subTravelOptionBenefits)[0]
    // Extract top-level benefit data
    if(isSpecialRT) {
        const findSubtravelOptionId = (id: string) => {
            // Check in J1 (onward) cards
            const j1Cards = cards?.J1 || []
            for (const card of j1Cards) {
                const subTravelOptionIds = card?.subTravelOptionIds || []
                if (subTravelOptionIds.includes(id)) {
                    return 'onward'
                }
            }
            
            // Check in J2 (return) cards
            const j2Cards = cards?.J2 || []
            for (const card of j2Cards) {
                const subTravelOptionIds = card?.subTravelOptionIds || []
                if (subTravelOptionIds.includes(id)) {
                    return 'return'
                }
            }
            
            return null // Not found in either
        }
        
        // Get all subTravelOptionIds from subTravelOptionBenefits
        const allSubTravelOptionIds = Object.keys(subTravelOptionBenefits)
        
        // Find which ones are onward and return
        allSubTravelOptionIds.forEach(id => {
            const journeyType = findSubtravelOptionId(id)
            if (journeyType === 'onward') {
                onwardsSubTravelOptionId = id
            } else if (journeyType === 'return') {
                returnSubTravelOptionId = id
            }
        })


        specialRtChargeInfos.onwardsChargeInfos = extractChargeInfosFromPenalties(subTravelOptionBenefits?.[onwardsSubTravelOptionId]?.benefits?.penalties || [])
        specialRtChargeInfos.returnChargeInfos = extractChargeInfosFromPenalties(subTravelOptionBenefits?.[returnSubTravelOptionId]?.benefits?.penalties || [])
    }
    
    // Extract sub-travel option benefit data
    const subTravelBenefitsData = subTravelOptionBenefits?.[subTravelOptionId]?.benefits || {}
    const {
        baggageAllowanceId: subTravelBaggageAllowanceId = '',
        benefits: subTravelBenefits = [],
        penalties: subTravelPenalties = [],
    } = subTravelBenefitsData
    
    // Helper function to select the appropriate value (prioritize top-level over sub-travel)
    const selectValue = (topLevel: any, subTravel: any) => {
        return !isEmpty(topLevel) ? topLevel : subTravel
    }
    
    return {
        baggageAllowanceId: selectValue(topLevelBaggageAllowanceId, subTravelBaggageAllowanceId),
        benefitIds: selectValue(topLevelBenefitIds, subTravelBenefits),
        penalty: selectValue(topLevelPenalty, subTravelPenalties),
        specialRtChargeInfos: isSpecialRT ? specialRtChargeInfos ??  {} : {}
    }
}

export const getBenefitProvided = (fare: any, servicesData: any, baggageAllowances: any) => {
    let benefitsProvided = {
        cancellation: '',
        dateChange: '',
        seat: '',
        meal: '',
        cabinBaggage: '',
        checkInBaggage: ''
    }
    const fareId = fare?.fareId
    const travelOptionId = fare?.travelOptionId
    // Prefer fare.benefits (flights-pwa InterstitialPage) — cancel/amend text ships on the
    // fare object from search/interstitial. cardIdToBenefitsMap is often still empty when
    // a_ff_coverage fires, which made a_cancellation_fee always fall through to 'N/A'.
    const fareBenefits = fare?.benefits || {}
    const cardServicesData: {
        baggageAllowanceId?: any
        benefitIds?: any[]
        penalty?: any[]
    } = getCardServicesData({ servicesData, travelOptionId, fareId }) || {}
    const benefitIds = !isEmpty(fareBenefits.benefitIds)
        ? fareBenefits.benefitIds
        : cardServicesData.benefitIds || []
    const penalty = !isEmpty(fareBenefits.penalty)
        ? fareBenefits.penalty
        : cardServicesData.penalty || []
    const baggageAllowanceId =
        fareBenefits.baggageAllowanceId || cardServicesData.baggageAllowanceId || ''
    const baggageSource = baggageAllowances || servicesData?.baggageAllowances || {}

    benefitIds.forEach((id: string) => {
        const benefit = servicesData?.benefits?.[id]?.benefitSummary?.[0]
        if (benefit) benefitsProvided[benefit.benefitType === 'SEAT' ? 'seat' : 'meal'] = benefit.text
    })

    baggageSource?.[baggageAllowanceId]?.benefitSummary?.forEach(({ benefitType, text, placeholderMap }) => {
        const formattedText = replacePlaceholders(text, placeholderMap).split('|')[0]
        benefitsProvided[benefitType === 'CHECK_IN_BAGGAGE' ? 'checkInBaggage' : 'cabinBaggage'] = formattedText
    })

    penalty.forEach(({ benefitSummary }) => {
        const { benefitType, text, placeholderMap } = benefitSummary?.[0] || {}
        if (benefitType === 'CANCEL') benefitsProvided.cancellation = replacePlaceholders(text, placeholderMap)
        if (benefitType === 'AMEND') benefitsProvided.dateChange = replacePlaceholders(text, placeholderMap)
    })
    return benefitsProvided
}
export const getFFCookieValue =  () => {
    const ffVar = Statsig?.getExperiment("fare_family_desktop_v7")?.get('variant', null)
    return `${ffVar || 'null'}`
}

export const getPtcTagData = (fare: FareType) => {
    const { benefitTags = [] } = fare || {}
    const tag = benefitTags?.find((benefit) => {
        return PTC_BENEFIT_TYPES.includes(benefit?.benefitType)
    }) || {}
    return tag || {};
}

export const getIntlTuppleTags = (fare: any) => {
    const { benefitTags = [], tags = [] } = fare || {}
    const combinedTagsList = [...benefitTags, ...tags]
    const tagList = combinedTagsList?.filter((benefit) => {
        return ALLOWED_BENEFIT_TYPES.includes(benefit?.benefitType)
    }) || []
    return tagList || [];
}

// Build seat/meal and baggage texts directly from fare + searchData for event Raven Events 
export const getEventDescriptionPartsFromFare = (fare: any, searchData: any) => {
    try {
        const seatMeal: string[] = []
        const baggageTexts: string[] = []
        if (!fare || !searchData) return { seatMeal, baggageTexts, tagTexts: [] }

        const { benefits: allBenefits = {}, baggageAllowances: allBaggageAllowances = {} } = searchData || {}
        const { benefits = {} } = fare || {}
        const { benefitIds = [], baggageAllowanceId = '' } = benefits || {}

        // Seat/Meal
        ;(benefitIds || []).forEach((id: string) => {
            const summary = allBenefits?.[id]?.benefitSummary?.[0]
            const bType = summary?.benefitType
            if (bType === 'SEAT') seatMeal.push( summary?.text || 'paid seat')
            if (bType === 'MEAL') seatMeal.push( summary?.text || 'paid meal')
        })

        // Baggage
        const allowance = allBaggageAllowances?.[baggageAllowanceId]
        allowance?.benefitSummary?.forEach?.(({ benefitType, text, placeholderMap }: any) => {
            const formatted = replacePlaceholders(text, placeholderMap).split('|')[0] || ''
            const match = formatted.match(/(\d{1,4})\s?kg/i)
            const weight = match ? `${match[1]} kg` : ''
            if (benefitType === 'HAND_BAGGAGE' && weight) baggageTexts.push(`${weight} cabin`)
            if (benefitType === 'CHECK_IN_BAGGAGE' && weight) baggageTexts.push(`${weight} check-in`)
        })

        const tagTexts = (getIntlTuppleTags(fare) || []).map((item: any) => item.displayText).filter(Boolean)
        return { seatMeal, baggageTexts, tagTexts }
    } catch (e) {
        return { seatMeal: [], baggageTexts: [], tagTexts: [] }
    }
}

export const composeEventDescription2FromFare = (fare: any, searchData: any) => {
    const { seatMeal, baggageTexts, tagTexts } = getEventDescriptionPartsFromFare(fare, searchData)
    return [...seatMeal, ...baggageTexts, ...tagTexts].filter(Boolean).join(' | ')
}
